import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getTranslatedProvince, getTranslatedRegion } from "@/utils/translations";
import { ArrowLeft, ArrowRight, Users, Globe, ExternalLink, Calendar, MapPin, FileText, Facebook, Instagram, Youtube, UserCheck, Search, Info, MessageCircle, Send, Lightbulb, BookOpen } from "lucide-react";
import { XIcon } from "@/components/icons/XIcon";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link, useParams, Navigate } from "react-router-dom";
import { allPartiesIncludingNonParliamentary, moroccoRegions } from "@/data/parties";
import { saveInteraction, isGlobalInteractionsEnabled, isGlobalQaEnabled, getBlockedPartiesForInteractions, fetchPublicQaFromServer, Interaction } from "@/data/interactionsStore";
import { usePartiesStore, getValidatedPartyDisplayData } from "@/data/partiesStore";
import { usePresentationsStore, getPresentationV2ForEntity } from "@/data/presentationsStore";
import { useToast } from "@/hooks/use-toast";
import { isPartyLikesEnabled } from "@/utils/featureFlags";
import { useEffect } from "react";
import { Heart } from "lucide-react";
import { incrementPartyMetric, decrementPartyMetric } from "@/data/partyMetricsStore";
import { useAuth } from "@/contexts/AuthContext";
import { addAlert } from "@/data/alertsStore";
import { toast as sonnerToast } from "sonner";
import { useGamificationStore } from "@/data/gamificationStore";
import SEO from "@/components/SEO";
import { fetchPublicCandidates } from "@/data/candidatesStore";
import { useCmsStore } from "@/data/cmsStore";

import { useEventsStore } from "@/data/eventsStore";

// Deleted regionsData and provincesData, we will use moroccoRegions.

const pageLabels = {
  fr: { back: "Retour aux partis", seats: "sièges au Parlement", focus: "Domaines d'intervention", events: "Événements du parti", noEvents: "Aucun événement programmé.", website: "Site officiel", program: "Programme du parti", presentation: "Présentation du parti", adhere: "Adhérer au Parti", candidates2026: "Candidats aux élections 2026", selectRegion: "Choisissez la région", selectProvince: "Choisissez la province ou préfecture", selectRegionPlaceholder: "Sélectionnez une région...", selectPlaceholder: "Sélectionnez une province...", noCandidates: "Veuillez sélectionner une province pour voir les candidats.", candidateList: "Candidats de la circonscription", socialNetworks: "Réseaux Sociaux", facebook: "Facebook", twitter: "X", instagram: "Instagram", youtube: "YouTube", eventsButton: "Consulter les événements des partis", backToParty: "Retour à ce parti", backToEvents: "Retour aux événements des partis", interact: "Interagir", interactTitle: "Intéragir avec le parti politique", submit: "Envoyer le message", partyPrinciples: "Principes et propositions du parti", readMore: "Lire la suite", structures: "Structures et Organisations du parti", youth: "Jeunesse du parti", youthNotSet: "Jeunesse (Non renseigné)", women: "Femmes du parti", womenNotSet: "Femmes (Non renseigné)", academy: "Académie du parti", academyNotSet: "Académie (Non renseigné)", qaBox: "Boîte à questions citoyennes", askQuestion: "Poser une question", noQuestions: "Aucune question publique pour le moment. Soyez le premier !", officialReply: "Réponse officielle", askPublicQuestion: "Poser une question publique", qaDesc: "Votre question sera soumise à la modération. Si elle est approuvée, elle apparaîtra publiquement ici de façon anonymisée, et le parti pourra y répondre.", qaPlaceholder: "Posez votre question avec respect et courtoisie...", qaTerms: "J'accepte que ma question soit publiée. Seuls mon prénom et l'initiale de mon nom seront visibles.", submitQa: "Soumettre ma question", contactCandidate: "Contacter", contactDesc: "Envoyez un message direct à ce candidat. Vos coordonnées lui seront transmises.", sendTo: "Envoyer à", noCandidatesRecorded: "En attente de consolidation", emailNotPublished: "(Ne sera pas publié)" },
  ar: { back: "العودة للأحزاب", seats: "مقاعد بالبرلمان", focus: "مجالات التدخل", events: "فعاليات الحزب", noEvents: "لا توجد فعاليات مبرمجة.", website: "الموقع الرسمي", program: "برنامج الحزب", presentation: "تقديم الحزب", adhere: "انخراط في الحزب", candidates2026: "مرشحو انتخابات 2026", selectRegion: "اختر الجهة", selectProvince: "اختر الإقليم أو العمالة", selectRegionPlaceholder: "اختر الجهة...", selectPlaceholder: "اختر الإقليم...", noCandidates: "الرجاء تحديد إقليم لرؤية المرشحين.", candidateList: "مرشحو الدائرة", socialNetworks: "الشبكات الاجتماعية", facebook: "فيسبوك", twitter: "إكس (تويتر)", instagram: "إنستغرام", youtube: "يوتيوب", eventsButton: "الاطلاع على فعاليات الأحزاب", backToParty: "العودة لهذا الحزب", backToEvents: "العودة لفعاليات الأحزاب", interact: "تفاعل", interactTitle: "تفاعل مع الحزب السياسي", submit: "إرسال الرسالة", partyPrinciples: "مبادئ ومقترحات الحزب", readMore: "اقرأ المزيد", structures: "هياكل ومنظمات الحزب", youth: "شبيبة الحزب", youthNotSet: "الشبيبة (غير متوفر)", women: "نساء الحزب", womenNotSet: "النساء (غير متوفر)", academy: "أكاديمية الحزب", academyNotSet: "الأكاديمية (غير متوفر)", qaBox: "صندوق أسئلة المواطنين", askQuestion: "اطرح سؤالا", noQuestions: "لا توجد أسئلة عامة حاليا. كن الأول!", officialReply: "رد رسمي", askPublicQuestion: "طرح سؤال عام", qaDesc: "سيتم عرض سؤالك على المشرفين. في حال الموافقة عليه، سيظهر هنا بشكل مجهول الهوية، وسيقوم الحزب بالإجابة عليه.", qaPlaceholder: "اطرح سؤالك باحترام ولباقة...", qaTerms: "أوافق على نشر سؤالي. سيظهر فقط اسمي الأول والحرف الأول من نسبي.", submitQa: "إرسال سؤالي", contactCandidate: "تواصل مع", contactDesc: "أرسل رسالة مباشرة إلى هذا المرشح. سيتم إرسال معلومات الاتصال الخاصة بك إليه.", sendTo: "إرسال إلى", noCandidatesRecorded: "في انتظار التجميع", emailNotPublished: "(لن يتم نشره)" },
  zgh: { back: "ⵓⵖⴰⵍ ⵙ ⵉⵎⵓⵔⴰⵏ", seats: "ⵉⵙⵓⵔⴰⵔ ⴳ ⵓⴱⵔⵍⴰⵎⴰⵏ", focus: "ⵉⴳⵔⴰⵏ ⵏ ⵓⵎⵀⴰⵍ", events: "ⵉⵎⵓⵙⵙⵓⵜⵏ ⵏ ⵓⵎⵓⵔ", noEvents: "ⵓⵔ ⵍⵍⵉⵏ ⵉⵎⵓⵙⵙⵓⵜⵏ.", website: "ⴰⵙⵉⵜ ⵓⵏⵚⵉⴱ", program: "ⴰⵖⴰⵡⴰⵙ ⵏ ⵓⵎⵓⵔ", presentation: "ⴰⵙⵎⵏⵉⴷ ⵏ ⵓⵎⵓⵔ", adhere: "ⴰⵎⴷⵔⴰⵡ ⴳ ⵓⵎⵓⵔ", candidates2026: "ⵉⵎⵙⵜⴰⵢⵏ ⵏ ⵉⵙⵜⴰⵢⵏ 2026", selectRegion: "ⵙⵜⵉ ⵜⴰⵎⵏⴰⴹⵜ ⵜⴰⵎⵇⵔⴰⵏⵜ", selectProvince: "ⵙⵜⵉ ⵜⴰⵎⵏⴰⴹⵜ ⵏⵏⴽ", selectRegionPlaceholder: "ⵙⵜⵉ ⵜⴰⵎⵏⴰⴹⵜ...", selectPlaceholder: "ⵙⵜⵉ ⵜⴰⵎⵏⴰⴹⵜ...", noCandidates: "ⵙⵜⵉ ⵜⴰⵎⵏⴰⴹⵜ ⴰⴼⴰⴷ ⴰⴷ ⵜⵥⵕⵎ ⵉⵎⵙⵜⴰⵢⵏ.", candidateList: "ⵉⵎⵙⵜⴰⵢⵏ ⵏ ⵜⵎⵏⴰⴹⵜ", socialNetworks: "ⵉⵥⵟⵡⴰⵏ ⵉⵏⴰⵎⵓⵏⵏ", facebook: "Facebook", twitter: "X", instagram: "Instagram", youtube: "YouTube", eventsButton: "ⵥⵕ ⵉⵎⵓⵙⵙⵓⵜⵏ ⵏ ⵉⵎⵓⵔⴰⵏ", backToParty: "ⵓⵖⴰⵍ ⵙ ⵓⵎⵓⵔ ⴰⴷ", backToEvents: "ⵓⵖⴰⵍ ⵙ ⵉⵎⵓⵙⵙⵓⵜⵏ ⵏ ⵉⵎⵓⵔⴰⵏ", interact: "ⴰⵎⵢⴰⵡⴰⴹ", interactTitle: "ⴰⵎⵢⴰⵡⴰⴹ ⴷ ⵓⵎⵓⵔ", submit: "ⴰⵣⵏ ⵜⴰⴱⵔⴰⵜ", partyPrinciples: "ⵉⵎⵏⵣⴰⵢⵏ ⴷ ⵉⵙⵓⵎⴰⵔ ⵏ ⵓⵎⵓⵔ", readMore: "ⵖⵔ ⵓⴳⴳⴰⵔ", structures: "ⵜⵓⵙⴽⵉⵡⵉⵏ ⴷ ⵜⵎⴰⴷⴷⴰⵙⵉⵏ ⵏ ⵓⵎⵓⵔ", youth: "ⵉⵄⵔⵔⵉⵎⵏ ⵏ ⵓⵎⵓⵔ", youthNotSet: "ⵉⵄⵔⵔⵉⵎⵏ (ⵓⵔ ⵉⵍⵍⵉ)", women: "ⵜⵉⵎⵖⴰⵔⵉⵏ ⵏ ⵓⵎⵓⵔ", womenNotSet: "ⵜⵉⵎⵖⴰⵔⵉⵏ (ⵓⵔ ⵉⵍⵍⵉ)", academy: "ⵜⴰⴽⴰⴷⵉⵎⵉⵜ ⵏ ⵓⵎⵓⵔ", academyNotSet: "ⵜⴰⴽⴰⴷⵉⵎⵉⵜ (ⵓⵔ ⵉⵍⵍⵉ)", qaBox: "ⴰⵙⵏⴷⵓⵇ ⵏ ⵉⵙⵇⵙⵉⵜⵏ ⵏ ⵉⵏⴰⵎⵓⵔⵏ", askQuestion: "ⵙⵇⵙⴰ ⵢⴰⵏ ⵓⵙⵇⵙⵉ", noQuestions: "ⵓⵔ ⵉⵍⵍⵉ ⴰⵡⴷ ⵢⴰⵏ ⵓⵙⵇⵙⵉ. ⴳ ⴰⵎⵣⵡⴰⵔⵓ!", officialReply: "ⴰⵔⵔⴰ ⵓⵏⵚⵉⴱ", askPublicQuestion: "ⵙⵇⵙⴰ ⵢⴰⵏ ⵓⵙⵇⵙⵉ ⴰⴳⴷⵓⴷⴰⵏ", qaDesc: "ⴰⵙⵇⵙⵉ ⵏⵏⴽ ⴰⴷ ⵉⵣⵔⵉ ⵖⵔ ⵉⵎⵙⵜⴳⴳⴰⵔⵏ. ⵉⵖ ⵉⵜⵜⵓⵇⴱⵍ, ⴰⴷ ⵉⴱⴰⵢⵏ ⴷⵖⵉ ⴱⵍⴰ ⵉⵙⵎ, ⴷ ⴰⴷ ⵉⵔⴰⵔ ⵓⵎⵓⵔ ⴼⵍⵍⴰⵙ.", qaPlaceholder: "ⵙⵇⵙⴰ ⴰⵙⵇⵙⵉ ⵏⵏⴽ ⵙ ⵓⵡⵇⵇⵔ ⴷ ⵜⵓⵙⵙⵏⴰ...", qaTerms: "ⵇⴱⵍⵖ ⴰⴷ ⵉⵜⵜⵓⴼⵙⵔ ⵓⵙⵇⵙⵉ ⵉⵏⵓ. ⵉⵙⵎ ⵉⵏⵓ ⴰⵎⵣⵡⴰⵔⵓ ⴷ ⵓⵙⴽⴽⵉⵍ ⴰⵎⵣⵡⴰⵔⵓ ⵏ ⵢⵉⵙⵎ ⵉⵏⵓ ⴽⴰ ⴰⴷ ⵉⵜⵜⵓⵢⴰⵏⵏⴰⵢⵏ.", submitQa: "ⴰⵣⵏ ⴰⵙⵇⵙⵉ ⵉⵏⵓ", contactCandidate: "ⴰⵎⵢⴰⵡⴰⴹ ⴷ", contactDesc: "ⴰⵣⵏ ⵜⴰⴱⵔⴰⵜ ⵉ ⵓⵎⵙⵜⴰⵢ ⴰⴷ. ⵉⵏⵖⵎⵉⵙⵏ ⵏⵏⴽ ⴰⴷ ⴰⵙ ⵜⵜⵓⵣⴰⵏⵏ.", sendTo: "ⴰⵣⵏ ⵉ", noCandidatesRecorded: "ⴰⵔ ⵉⵜⵜⵓⵔⵊⴰ ⴰⵙⵎⵓⵏ", emailNotPublished: "(ⵓⵔ ⴰⴷ ⵉⵜⵜⵓⴼⵙⵔ)" },
};

const PartiDetailPage = () => {
  const { lang, isRtl, t } = useI18n();
  const { acronym } = useParams<{ acronym: string }>();
  const profiles = usePartiesStore(state => state.profiles);
  const presentations = usePresentationsStore(state => state.presentations);
  const [selectedRegionId, setSelectedRegionId] = useState<string>("");
  const [selectedProvinceId, setSelectedProvinceId] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [interactionRegion, setInteractionRegion] = useState<string>("");
  const [messageCandidate, setMessageCandidate] = useState<any>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [partyLikesOn, setPartyLikesOn] = useState(isPartyLikesEnabled());
  const [globalInteractionsEnabled, setGlobalInteractionsEnabled] = useState(isGlobalInteractionsEnabled());
  const [globalQaEnabled, setGlobalQaEnabled] = useState(isGlobalQaEnabled());
  const [blockedParties, setBlockedParties] = useState(getBlockedPartiesForInteractions());
  const [isInteractModalOpen, setIsInteractModalOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { addXp, unlockBadge } = useGamificationStore();
  
  const [publicCandidates, setPublicCandidates] = useState<any[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  

  const getTranslatedCategory = (cat: string, lang: string) => {
      if (cat === "Autre") {
          if (lang === 'ar') return "أخرى";
          if (lang === 'zgh') return "ⵢⴰⴹⵏ";
      }
      return cat;
  };

  const getCategoryLabel = (lang: string) => {
      if (lang === 'ar') return "الفئة :";
      if (lang === 'zgh') return "ⵜⴰⵙⵎⵉⵍⵜ :";
      return "Catégorie :";
  };



  const candidateMatchesSearch = (c: any) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const fullName = `${c.prenomCandidat || c.firstName || ""} ${c.nomCandidat || c.lastName || ""}`.toLowerCase();
    const fullNameAr = `${c.prenomCandidatAr || c.firstNameAr || ""} ${c.nomCandidatAr || c.lastNameAr || ""}`.toLowerCase();
    const provinceMatch = (c.province || "").toLowerCase().includes(q);
    const regionMatch = (c.region || "").toLowerCase().includes(q);
    const typeMatchLocale = c.listType === "locale" && (isRtl ? "لائحة محلية" : "liste locale").includes(q);
    const typeMatchRegionale = c.listType === "regionale" && (isRtl ? "لائحة جهوية" : "liste régionale").includes(q);
    
    return fullName.includes(q) || fullNameAr.includes(q) || provinceMatch || regionMatch || typeMatchLocale || typeMatchRegionale;
  };
  
  const provincesForDropdown = useMemo(() => {
    if (selectedRegionId && selectedRegionId !== "all") {
      return moroccoRegions.find(r => r.name === selectedRegionId || r.nameAr === selectedRegionId)?.provinces || [];
    }
    return moroccoRegions.flatMap(r => r.provinces);
  }, [selectedRegionId]);

  const [publicQa, setPublicQa] = useState<Interaction[]>([]);
  const [isQaModalOpen, setIsQaModalOpen] = useState(false);

  const { events: storeEvents, fetchEvents } = useEventsStore();

  useEffect(() => {
    fetchEvents(true); // Fetch public approved events
  }, [fetchEvents]);

  const cmsLayouts = useCmsStore(state => state.layouts);

  useEffect(() => {
    if (isDialogOpen && acronym) {
      setIsLoadingCandidates(true);
      fetchPublicCandidates().then(data => {
        const filtered = data.filter(c => c.partyAcronym?.toLowerCase() === acronym?.toLowerCase()).map(c => ({
          ...c,
          name: (lang === "ar" && (c.firstNameAr || c.lastNameAr)) ? `${c.firstNameAr || ""} ${c.lastNameAr || ""}`.trim() : `${c.firstName || ""} ${c.lastName || ""}`.trim(),
          role: { 
            fr: c.listType === 'regionale' ? 'Liste Régionale' : 'Liste Locale',
            ar: c.listType === 'regionale' ? 'لائحة جهوية' : 'لائحة محلية',
            zgh: c.listType === 'regionale' ? 'ⵜⴰⵍⴳⴰⵎⵜ ⵜⴰⵎⵏⴰⴹⵜ' : 'ⵜⴰⵍⴳⴰⵎⵜ ⵜⴰⴷⵖⴰⵔⴰⵏⵜ'
          }
        }));
        setPublicCandidates(filtered);
        setIsLoadingCandidates(false);
      });
    }
  }, [isDialogOpen, acronym]);

  useEffect(() => {
    const handleLikesChange = (e: any) => setPartyLikesOn(e.detail?.enabled ?? true);
    const handleGlobalInteractions = () => setGlobalInteractionsEnabled(isGlobalInteractionsEnabled());
    const handleGlobalQa = () => setGlobalQaEnabled(isGlobalQaEnabled());
    const handlePartyInteractions = () => setBlockedParties(getBlockedPartiesForInteractions());
    
    window.addEventListener("partyLikesChanged", handleLikesChange);
    window.addEventListener("interactionsGlobalSettingsChanged", handleGlobalInteractions);
    window.addEventListener("interactionsGlobalQaSettingsChanged", handleGlobalQa);
    window.addEventListener("interactionsPartySettingsChanged", handlePartyInteractions);
    
    if (acronym) {
      incrementPartyMetric(acronym, "presentation");
      
      // Fetch public Q&A
      fetchPublicQaFromServer(acronym).then(data => setPublicQa(data));
      
      // Récompense Gamification (si c'est un visiteur/citoyen)
      if (!user || user.role === 'CITIZEN') {
        // On donne 5 XP par visite de parti (limité par les règles métier plus tard si besoin, mais on laisse simple ici)
        addXp(10, "Visite d'un profil politique");
        unlockBadge('informed_citizen');
      }
    }

    return () => {
      window.removeEventListener("partyLikesChanged", handleLikesChange);
      window.removeEventListener("interactionsGlobalSettingsChanged", handleGlobalInteractions);
      window.removeEventListener("interactionsGlobalQaSettingsChanged", handleGlobalQa);
      window.removeEventListener("interactionsPartySettingsChanged", handlePartyInteractions);
    };
  }, [acronym]);
  
  const interactionProvinces = interactionRegion
    ? moroccoRegions.find(r => r.name === interactionRegion)?.provinces || []
    : [];
  
  const handleRegionChange = (val: string) => {
    setInteractionRegion(val);
  };

  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const l = pageLabels[lang];

  const party = allPartiesIncludingNonParliamentary.find(p => p.acronym === acronym);
  if (!party) return <Navigate to="/" replace />;

  const partyName = t.parties.partiesNames[party.acronym] || party.acronym;
  const partyFocus = t.parties.partiesFocus[party.acronym] || "";
  const partyEvents = storeEvents.filter(e => e.partyAcronym === party.acronym && e.status === "approved" && e.publishStatus !== "draft");
  const pData = getValidatedPartyDisplayData(party.acronym, partyName, partyFocus, lang as any);
  
  // Remplacer les noms et focus hardcodés par ceux de la DB
  const displayPartyName = pData.fullName || partyName;
  const displayPartyFocus = pData.focus || partyFocus;
  
  const v2Data = getPresentationV2ForEntity(party.acronym, "PARTY");
  const displayLogoUrl = v2Data?.logoUrl || pData.logoUrl;
  const displayWebsite = v2Data?.website || pData.website;
  const displaySocials = {
    ...(pData.socials || {}),
    ...(v2Data?.facebook ? { facebook: v2Data.facebook } : {}),
    ...(v2Data?.twitter ? { twitter: v2Data.twitter } : {}),
    ...(v2Data?.instagram ? { instagram: v2Data.instagram } : {}),
    ...(v2Data?.youtube ? { youtube: v2Data.youtube } : {}),
  };
  const displayDescription = v2Data?.description || displayPartyFocus;
  const hasV2Details = v2Data?.activites || v2Data?.contribution || v2Data?.projets || v2Data?.image1Url || v2Data?.image2Url;
  const displayProgramUrl = pData.programmePdfUrl || pData.programUrl;
  const displayPresentationUrl = pData.presentationUrl;
  
  const selectedProvinceTitle = selectedProvinceId || "";

  const handleToggleLike = () => {
    if (isLiked) {
      setIsLiked(false);
      decrementPartyMetric(party.acronym, "like");
      sonnerToast("Retiré des favoris", { icon: "💔" });
    } else {
      setIsLiked(true);
      incrementPartyMetric(party.acronym, "like");
      sonnerToast("Ajouté aux favoris", { icon: "❤️" });
      
      if (user && user.role === 'citizen') {
        addAlert({
          title: { fr: `Abonnement : ${partyName}`, ar: `اشتراك : ${partyName}`, zgh: `ⴰⵎⴷⵔⴰⵡ : ${partyName}` },
          description: { 
            fr: `Vous suivez désormais le parti ${partyName}. Vous serez notifié de ses actualités.`, 
            ar: `أنت الآن تتابع حزب ${partyName}. سيتم إعلامك بأخباره.`, 
            zgh: `ⵜⴹⴼⵕⴷ ⴰⵎⵓⵔ ${partyName}. ⴰⴷ ⵜⵜⵓⵙⵎⴽⵜⵉⴷ ⵙ ⵉⵏⵖⵎⵉⵙⵏ ⵏⵏⵙ.` 
          },
          sourceName: partyName,
          sourceType: "Parti Politique",
          region: "Toutes",
          theme: "Politique",
          isImportant: false,
          targetUserId: user.id,
          notificationType: "reminder_event",
          linkUrl: window.location.href
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={`Kayn'in | Découvrez le profil du ${displayPartyName}`}
        description={displayDescription || `Consultez le programme, les candidats et interagissez avec les représentants de ${displayPartyName}.`}
        image={displayLogoUrl || "https://kaynin.ma/og-image.jpg"}
        url={`https://kaynin.ma/parti/${party.acronym}`}
      />
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="container max-w-3xl">
          <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
            <Link to="/partis">
              <BackArrow className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
              {l.back}
            </Link>
          </Button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Party Header */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8 text-center md:text-left rtl:md:text-right bg-card border border-border/50 rounded-3xl p-6 md:p-8 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-full h-1.5" style={{ background: party.color }} />
              
              <div className="w-24 h-24 shrink-0 flex items-center justify-center p-2 rounded-2xl bg-muted/40 shadow-inner">
                 {displayLogoUrl ? (
                    <img src={displayLogoUrl} alt={`Logo ${party.acronym}`} className="w-full h-full object-contain filter drop-shadow-sm" />
                  ) : (
                    <span className="text-4xl drop-shadow-sm">{party.symbol}</span>
                 )}
              </div>
              
              <div className="flex-1">
                <div className="inline-flex items-center justify-center text-xs font-body font-bold px-3 py-1 mb-3 rounded-full text-white shadow-sm" style={{ background: party.color }}>
                  {lang === 'ar' && party.acronymAr ? party.acronymAr : party.acronym}
                </div>
                <h1 className="text-2xl md:text-3xl font-display font-extrabold text-foreground leading-tight mb-2">
                  {lang === 'ar' && displayPartyName === party.acronymAr ? partyName : displayPartyName}
                </h1>
                
                {party.seats > 0 && (
                  <Badge className="bg-primary/10 text-primary text-xs font-body px-3 py-1 mt-1 border-primary/20">
                    <Users className="w-3.5 h-3.5 mr-1.5" />{party.seats} {l.seats}
                  </Badge>
                )}
              </div>
              
              {partyLikesOn && (
                <div className="md:ml-auto md:self-start mt-2 md:mt-0 relative group shrink-0">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={handleToggleLike} 
                    className={`w-12 h-12 rounded-full border-2 shadow-sm transition-all duration-300 hover:scale-105 ${
                      isLiked ? "border-pink-500 bg-pink-50 hover:bg-pink-100" : "border-border/60 hover:border-pink-200 hover:bg-pink-50/50"
                    }`}
                  >
                    <Heart className={`w-6 h-6 transition-colors duration-300 ${isLiked ? "fill-pink-500 text-pink-500" : "text-muted-foreground group-hover:text-pink-400"}`} />
                  </Button>
                </div>
              )}
            </div>

            {/* Links and Socials */}
            <div className="flex flex-col gap-4 mb-10 pb-8 border-b border-border/60">
              {/* Ligne du haut : Site, Programme, Présentation */}
              <div className="flex flex-wrap gap-4 justify-center md:justify-start items-center">
                {displayWebsite ? (
                  <a href={displayWebsite} target="_blank" rel="noopener noreferrer" className="group" onClick={() => incrementPartyMetric(party.acronym, "website")}>
                    <Button variant="outline" className="font-body gap-2 shadow-sm rounded-full bg-card hover:bg-muted font-semibold transition-all hover:-translate-y-0.5" style={{ borderColor: party.color, color: party.color }}>
                      <Globe className="w-4 h-4" /> {l.website} <ExternalLink className="w-3 h-3 opacity-50 transition-opacity group-hover:opacity-100" />
                    </Button>
                  </a>
                ) : (
                  <Button variant="outline" className="font-body gap-2 shadow-sm rounded-full bg-card font-semibold opacity-50 cursor-not-allowed" style={{ borderColor: party.color, color: party.color }} onClick={(e) => { e.preventDefault(); incrementPartyMetric(party.acronym, "website"); }}>
                    <Globe className="w-4 h-4" /> {l.website}
                  </Button>
                )}
                
                {displayProgramUrl ? (
                  <a href={displayProgramUrl} target="_blank" rel="noopener noreferrer" className="group" onClick={() => incrementPartyMetric(party.acronym, "program")}>
                    <Button variant="outline" className="font-body gap-2 shadow-sm rounded-full bg-card hover:bg-muted font-semibold transition-all hover:-translate-y-0.5" style={{ borderColor: party.color, color: party.color }}>
                      <FileText className="w-4 h-4" /> {l.program} <ExternalLink className="w-3 h-3 opacity-50 transition-opacity group-hover:opacity-100" />
                    </Button>
                  </a>
                ) : (
                  <Button variant="outline" className="font-body gap-2 shadow-sm rounded-full bg-card font-semibold opacity-50 cursor-not-allowed" style={{ borderColor: party.color, color: party.color }} onClick={(e) => { e.preventDefault(); incrementPartyMetric(party.acronym, "program"); }}>
                    <FileText className="w-4 h-4" /> {l.program}
                  </Button>
                )}
                
                {displayPresentationUrl ? (
                  <a href={displayPresentationUrl} target="_blank" rel="noopener noreferrer" className="group" onClick={() => incrementPartyMetric(party.acronym, "presentation")}>
                    <Button variant="outline" className="font-body gap-2 shadow-sm rounded-full bg-card hover:bg-muted font-semibold transition-all hover:-translate-y-0.5" style={{ borderColor: party.color, color: party.color }}>
                      <Info className="w-4 h-4" /> {l.presentation} <ExternalLink className="w-3 h-3 opacity-50 transition-opacity group-hover:opacity-100" />
                    </Button>
                  </a>
                ) : (
                  <Button variant="outline" className="font-body gap-2 shadow-sm rounded-full bg-card font-semibold opacity-50 cursor-not-allowed" style={{ borderColor: party.color, color: party.color }} onClick={(e) => { e.preventDefault(); incrementPartyMetric(party.acronym, "presentation"); }}>
                    <Info className="w-4 h-4" /> {l.presentation}
                  </Button>
                )}
              </div>

              {/* Ligne du bas : Réseaux sociaux, Bouton Interagir */}
              <div className="flex flex-wrap gap-4 justify-center md:justify-start items-center mt-2">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(displaySocials).map(([net, url]) => (
                    <a key={net} href={url as string} target="_blank" rel="noopener noreferrer" onClick={() => incrementPartyMetric(party.acronym, net as any)}>
                      <Button variant="ghost" size="icon" className="rounded-full shadow-sm bg-muted/50 border border-transparent hover:border-border hover:bg-card hover:-translate-y-0.5 transition-all w-10 h-10">
                        {net === 'facebook' && <Facebook className="w-4 h-4 text-blue-600" />}
                        {net === 'twitter' && <XIcon className="w-4 h-4 text-foreground" />}
                        {net === 'instagram' && <Instagram className="w-4 h-4 text-pink-600" />}
                        {net === 'youtube' && <Youtube className="w-4 h-4 text-red-600" />}
                        <span className="sr-only">{net}</span>
                      </Button>
                    </a>
                  ))}
                </div>

                {globalInteractionsEnabled && !blockedParties.includes(party.acronym) && (
                 <Dialog open={isInteractModalOpen} onOpenChange={setIsInteractModalOpen}>
                   <DialogTrigger asChild>
                     <Button className="font-body gap-2 shadow-md rounded-full text-white font-semibold transition-all hover:-translate-y-0.5 ml-0 sm:ml-2" style={{ backgroundColor: party.color }}>
                       <MessageCircle className="w-4 h-4" /> {l.interact}
                     </Button>
                   </DialogTrigger>
                   <DialogContent className="max-w-2xl w-[95vw] rounded-3xl p-0 shadow-2xl border-none overflow-hidden">
                      <div className="h-2 w-full" style={{ background: party.color }} />
                      <DialogHeader className="px-6 pt-6 pb-2 text-left">
                        <DialogTitle className="font-display text-2xl flex items-center gap-2 mb-2 font-bold" style={{ color: party.color }}>
                          <MessageCircle className="w-6 h-6" />
                          {l.interactTitle}
                        </DialogTitle>
                      <DialogDescription className="font-body text-muted-foreground">
                        {t.engagement?.subtitle || "Remplissez ce formulaire pour envoyer un message directement aux responsables locaux ou nationaux du parti politique."}
                      </DialogDescription>
                    </DialogHeader>
                   <form onSubmit={(e) => {
                     e.preventDefault();
                     const fd = new FormData(e.currentTarget);
                     saveInteraction({
                       type: "party",
                       candidateName: "Responsable du Parti",
                       partyAcronym: party.acronym,
                       senderLastName: fd.get("lastName") as string,
                       senderFirstName: fd.get("firstName") as string,
                       senderEmail: fd.get("email") as string,
                       senderPhone: "",
                       region: interactionRegion,
                       province: fd.get("province") as string,
                       subject: fd.get("subject") as string,
                       message: fd.get("message") as string,
                     });
                     toast({
                       title: "Message envoyé",
                       description: `Votre message a bien été envoyé au parti ${party.acronym}.`,
                     });
                     setIsInteractModalOpen(false);
                   }} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar bg-card">
                     <div className="grid md:grid-cols-2 gap-4">
                       <div>
                         <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.lastName || "Nom"} *</label>
                         <input required name="lastName" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                       </div>
                       <div>
                         <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.firstName || "Prénom"} *</label>
                         <input required name="firstName" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                       </div>
                     </div>
                     <div className="grid md:grid-cols-2 gap-4">
                       <div>
                         <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.region || "Région"} *</label>
                         <select 
                           value={interactionRegion}
                           onChange={(e) => handleRegionChange(e.target.value)}
                           className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1" style={{ '--tw-ring-color': party.color } as any}>
                            <option value="">{l.selectRegionPlaceholder}</option>
                            {moroccoRegions.map(r => <option key={r.name} value={r.name}>{isRtl && r.nameAr ? r.nameAr : r.name}</option>)}
                         </select>
                       </div>
                       <div>
                         <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.province || "Province"} *</label>
                         <select 
                           required name="province"
                           disabled={!interactionRegion}
                           className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 disabled:opacity-50" style={{ '--tw-ring-color': party.color } as any}>
                            <option value="">{l.selectPlaceholder}</option>
                            {interactionProvinces.map(p => <option key={p} value={p}>{getTranslatedProvince(p, lang)}</option>)}
                         </select>
                       </div>
                     </div>
                     <div>
                       <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.email || "Email"} *</label>
                       <input required name="email" type="email" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                     </div>
                     <div>
                       <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.object || "Objet"} *</label>
                       <input required name="subject" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                     </div>
                     <div>
                       <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.message || "Message"} *</label>
                       <textarea required name="message" className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                     </div>
                     <div className="flex items-start gap-3 mt-2">
                       <Checkbox required id="terms" className="mt-1" />
                       <label htmlFor="terms" className="text-sm font-body text-foreground leading-relaxed cursor-pointer">
                         {t.engagement?.declaration || "J'atteste par la présente que les informations fournies sont exactes."}
                       </label>
                     </div>
                     <Button type="submit" className="w-full gap-2 font-body font-semibold text-lg py-6 mt-4 text-white shadow-md hover:shadow-lg transition-all border-none" style={{ background: party.color }}>
                       <Send className="w-5 h-5" />
                       {l.submit}
                     </Button>
                   </form>
                 </DialogContent>
               </Dialog>
              )}
              {pData.adhesionUrl && (
                <a href={pData.adhesionUrl} target="_blank" rel="noopener noreferrer" onClick={() => incrementPartyMetric(party.acronym, "adhesion" as any)}>
                  <Button className="font-body gap-2 shadow-md rounded-full text-white font-semibold transition-all hover:-translate-y-0.5 ml-0 sm:ml-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 border-none">
                    <UserCheck className="w-4 h-4" /> {l.adhere}
                  </Button>
                </a>
              )}
              </div>
            </div>

            {/* Candidates 2026 Action Button */}
            <div className="mb-12">
               <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                 <DialogTrigger asChild>
                   <Button 
                      className="w-full text-lg py-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 font-display font-bold relative overflow-hidden group text-white border-none"
                      style={{ background: `linear-gradient(135deg, ${party.color}E6 0%, ${party.color} 100%)` }}
                   >
                     <div className="absolute inset-0 bg-black/10 transition-opacity opacity-0 group-hover:opacity-100"></div>
                     <UserCheck className="w-6 h-6 mr-3 drop-shadow-md transition-transform group-hover:scale-110" />
                     <span className="drop-shadow-sm">{l.candidates2026}</span>
                     <ArrowRight className="w-5 h-5 ml-auto drop-shadow-sm opacity-70 group-hover:translate-x-1 transition-transform" />
                   </Button>
                 </DialogTrigger>
                 <DialogContent className="max-w-xl w-[95vw] rounded-3xl overflow-hidden p-0 gap-0 shadow-2xl border-none">
                    <div className="h-2 w-full" style={{ background: party.color }} />
                    <DialogHeader className="px-6 pt-6 pb-2 text-left">
                      <DialogTitle className="font-display text-2xl flex items-center gap-2 mb-2 font-bold">
                        <UserCheck className="w-6 h-6" style={{ color: party.color }} />
                        {l.candidates2026}
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="px-6 py-4 space-y-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
                      
                      {/* Region and Province Selection */}
                      <div className="bg-muted/30 p-4 rounded-2xl space-y-4 border border-border/50">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-foreground px-1">{l.selectRegion}</label>
                          <Select onValueChange={(val) => { setSelectedRegionId(val); setSelectedProvinceId("all"); }} value={selectedRegionId}>
                            <SelectTrigger className="w-full h-12 rounded-xl text-base font-body border-border/80 focus:ring-1 bg-background hover:bg-muted/10 transition-colors">
                              <SelectValue placeholder={l.selectRegionPlaceholder} />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border-border/80 shadow-elevated">
                              <SelectItem value="all">{isRtl ? "جميع الجهات" : "Toutes les régions"}</SelectItem>
                              {moroccoRegions.map(reg => (
                                <SelectItem key={reg.name} value={reg.name} className="font-body text-base px-4 py-3 cursor-pointer">
                                  {isRtl && reg.nameAr ? reg.nameAr : reg.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-foreground px-1">{l.selectProvince}</label>
                          <Select onValueChange={setSelectedProvinceId} value={selectedProvinceId} disabled={!selectedRegionId || selectedRegionId === 'all'}>
                            <SelectTrigger className="w-full h-12 rounded-xl text-base font-body border-border/80 focus:ring-1 bg-background hover:bg-muted/10 transition-colors">
                              <SelectValue placeholder={l.selectPlaceholder} />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border-border/80 shadow-elevated">
                              <SelectItem value="all">{isRtl ? "جميع العمالات" : "Toutes les préfectures"}</SelectItem>
                              {provincesForDropdown.map(prov => (
                                <SelectItem key={prov} value={prov} className="font-body text-base px-4 py-3 cursor-pointer">
                                  {getTranslatedProvince(prov, lang)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="bg-muted/30 p-4 rounded-2xl space-y-4 border border-border/50">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-foreground px-1">{isRtl ? "نوع اللائحة" : "Type de liste"}</label>
                          <Select onValueChange={setFilterType} value={filterType}>
                            <SelectTrigger className="w-full h-12 rounded-xl text-base font-body border-border/80 focus:ring-1 bg-background hover:bg-muted/10 transition-colors">
                              <SelectValue placeholder={isRtl ? "نوع اللائحة" : "Type de liste"} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">{isRtl ? "جميع الأنواع" : "Tous les types"}</SelectItem>
                              <SelectItem value="locale">{isRtl ? "لائحة محلية" : "Liste locale"}</SelectItem>
                              <SelectItem value="regionale">{isRtl ? "لائحة جهوية" : "Liste régionale"}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="relative w-full">
                          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <input 
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={isRtl ? "بحث..." : "Recherche Rapide..."}
                            className="w-full bg-background border border-border rounded-xl pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow"
                          />
                        </div>
                      </div>

                      <AnimatePresence mode="wait">
                          <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-card rounded-2xl p-5 border border-border shadow-sm"
                          >
                            <div className="flex items-center justify-between mb-5 pb-3 border-b border-border/50">
                              <h4 className="font-display font-semibold text-lg text-foreground flex items-center gap-2">
                                <Users className="w-5 h-5 text-primary" /> {l.candidateList}
                              </h4>
                              {(selectedProvinceTitle && selectedProvinceTitle !== "all") && (
                                <Badge variant="secondary" className="font-body font-medium bg-muted/60 text-muted-foreground">
                                  <MapPin className="w-3.5 h-3.5 mr-1" /> {selectedProvinceTitle}
                                </Badge>
                              )}
                            </div>
                            
                            <div className="space-y-4">
                              {isLoadingCandidates ? (
                                <div className="flex justify-center py-8">
                                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                                </div>
                              ) : (() => {
                                let filtered = publicCandidates;
                                if (selectedRegionId && selectedRegionId !== "all") {
                                  filtered = filtered.filter(c => c.region === selectedRegionId);
                                }
                                if (selectedProvinceId && selectedProvinceId !== "all") {
                                  filtered = filtered.filter(c => c.province === selectedProvinceId);
                                }
                                if (filterType && filterType !== "all") {
                                  filtered = filtered.filter(c => c.listType === filterType);
                                }
                                if (searchQuery) {
                                  filtered = filtered.filter(candidateMatchesSearch);
                                }
                                
                                if (filtered.length === 0) {
                                  return <p className="text-sm text-muted-foreground">{l.noCandidatesRecorded}</p>;
                                }
                                
                                return filtered.map(c => (
                                <div key={c.id} className="flex flex-col sm:flex-row gap-4 bg-background p-4 rounded-xl border border-border/60 hover:shadow-md transition-shadow group">
                                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-muted overflow-hidden shrink-0 shadow-inner flex items-center justify-center relative">
                                    {c.photoUrl ? (
                                      <img src={c.photoUrl} alt="Candidat" className="w-full h-full object-cover" />
                                    ) : (
                                      <div 
  className="w-full h-full flex items-center justify-center text-white font-display font-bold text-3xl shadow-inner mix-blend-multiply" 
  style={{ backgroundColor: party.color || 'hsl(var(--primary))' }}
>
  {`${(c.firstName || c.prenomCandidat || "").charAt(0)}${(c.lastName || c.nomCandidat || "").charAt(0)}`.toUpperCase() || c.name.charAt(0).toUpperCase()}
</div>
                                    )}
                                    <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                  </div>
                                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                                    <div className="flex justify-between items-start">
                                      <Badge className="w-fit mb-2 bg-primary/10 text-primary border-primary/20 text-[10px] font-bold px-2 py-0.5">
                                        {c.listType === 'regionale' ? (isRtl ? 'لائحة جهوية' : 'Liste Régionale') : (isRtl ? 'لائحة محلية' : 'Liste Locale')}
                                      </Badge>
                                      
                                      <div className="flex gap-2 relative z-10">
                                        {c.whatsapp && (
                                          <a href={`https://wa.me/${c.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:scale-110 transition-transform p-1 bg-green-50 rounded-full" title="Contacter sur WhatsApp">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                          </a>
                                        )}
                                        {globalInteractionsEnabled && !blockedParties.includes(party.acronym) && c.email && (
                                          <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 py-0 border-primary/30 text-primary hover:bg-primary/10 gap-1 rounded-full cursor-pointer" onClick={() => setMessageCandidate(c)}>
                                            <MessageCircle className="w-3 h-3" /> {l.interact}
                                          </Button>
                                        )}
                                      </div>
                                    </div>

                                    <h5 className="font-display font-bold text-lg text-foreground mb-1 group-hover:text-primary transition-colors">
                                      {(lang === "ar" && (c.prenomCandidatAr || c.firstNameAr || c.nomCandidatAr || c.lastNameAr)) ? `${c.prenomCandidatAr || c.firstNameAr || ""} ${c.nomCandidatAr || c.lastNameAr || ""}`.trim() : `${c.prenomCandidat || c.firstName || ""} ${c.nomCandidat || c.lastName || ""}`.trim()}
                                    </h5>
                                    
                                    {c.province ? (
                                       <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-body mb-2">
                                         <MapPin className="w-3.5 h-3.5 text-primary/60" /> {getTranslatedProvince(c.province, lang)}
                                       </div>
                                    ) : c.region ? (
                                       <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-body mb-2">
                                         <MapPin className="w-3.5 h-3.5 text-primary/60" /> {getTranslatedRegion(c.region, lang)}
                                       </div>
                                    ) : null}
                                    {c.categorieSociopro && (
                                       <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-body mb-2">
                                         {getCategoryLabel(lang)} {getTranslatedCategory(c.categorieSociopro, lang)}
                                       </div>
                                    )}

                                    <p className="font-body text-xs text-muted-foreground leading-relaxed line-clamp-3">
                                      {typeof c.bio === 'string' ? c.bio : (c.bio?.[lang as 'fr'|'ar'|'zgh'] || c.bio?.fr || (isRtl ? "لا توجد سيرة ذاتية" : "Aucune biographie."))}
                                    </p>
                                    
                                    <div className="flex gap-2 mt-2">
                                      {c.facebook && <a href={c.facebook} target="_blank" className="text-muted-foreground hover:text-[#1877F2]"><Facebook className="w-3.5 h-3.5"/></a>}
                                      {c.twitter && <a href={c.twitter} target="_blank" className="text-muted-foreground hover:text-foreground"><XIcon className="w-3.5 h-3.5"/></a>}
                                      {c.instagram && <a href={c.instagram} target="_blank" className="text-muted-foreground hover:text-[#E4405F]"><Instagram className="w-3.5 h-3.5"/></a>}
                                    </div>
                                  </div>
                                </div>
                                ));
                              })()}
                            </div>
                          </motion.div>
                      </AnimatePresence>

                      {/* the 3 Action Links as requested */}
                      <div className="flex flex-col gap-2 mt-6 pt-4 border-t border-border/50">
                        <Button variant="ghost" onClick={() => setIsDialogOpen(false)} className="w-full font-body justify-start text-muted-foreground hover:text-foreground">
                          <ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" /> {l.backToParty}
                        </Button>
                        <Button variant="ghost" asChild className="w-full font-body justify-start text-muted-foreground hover:text-foreground">
                           <Link to="/partis" onClick={() => setIsDialogOpen(false)}>
                             <Users className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" /> {l.back}
                           </Link>
                        </Button>
                        <Button variant="ghost" asChild className="w-full font-body justify-start text-muted-foreground hover:text-foreground">
                           <Link to="/agenda-national" onClick={() => setIsDialogOpen(false)}>
                             <Calendar className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" /> {l.backToEvents}
                           </Link>
                        </Button>
                      </div>

                    </div>
                 </DialogContent>
               </Dialog>

               {/* Modal pour envoyer un message à un candidat spécifique */}
               {messageCandidate && (
                 <Dialog open={!!messageCandidate} onOpenChange={(open) => !open && setMessageCandidate(null)}>
                   <DialogContent className="max-w-2xl w-[95vw] rounded-3xl p-0 shadow-2xl border-none overflow-hidden" style={{ zIndex: 60 }}>
                     <div className="h-2 w-full" style={{ background: party.color }} />
                     <DialogHeader className="px-6 pt-6 pb-2 text-left">
                       <DialogTitle className="font-display text-2xl flex items-center gap-2 mb-2 font-bold" style={{ color: party.color }}>
                         <MessageCircle className="w-6 h-6" />
                         {l.contactCandidate} {messageCandidate.name}
                       </DialogTitle>
                       <DialogDescription className="font-body text-muted-foreground">
                         {l.contactDesc}
                       </DialogDescription>
                     </DialogHeader>
                     <form onSubmit={(e) => {
                       e.preventDefault();
                       const fd = new FormData(e.currentTarget);
                       saveInteraction({
                         type: "candidate",
                         candidateId: messageCandidate.id,
                         candidateName: messageCandidate.name,
                         partyAcronym: party.acronym,
                         senderLastName: fd.get("lastName") as string,
                         senderFirstName: fd.get("firstName") as string,
                         senderEmail: fd.get("email") as string,
                         senderPhone: "",
                         region: interactionRegion || selectedRegionId,
                         province: selectedProvinceId,
                         subject: fd.get("subject") as string,
                         message: fd.get("message") as string,
                       });
                       toast({
                         title: "Message envoyé",
                         description: `Votre message a bien été envoyé à ${messageCandidate.name}.`,
                       });
                       setMessageCandidate(null);
                     }} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar bg-card text-left">
                       <div className="grid md:grid-cols-2 gap-4">
                     <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.lastName || "Nom"} *</label>
                          <input required name="lastName" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                        </div>
                        <div>
                          <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.firstName || "Prénom"} *</label>
                          <input required name="firstName" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.email || "Email"} *</label>
                        <input required name="email" type="email" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                      </div>
                       </div>
                       <div>
                         <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.object || "Objet"} *</label>
                         <input required name="subject" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                       </div>
                       <div>
                         <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.message || "Message"} *</label>
                         <textarea required name="message" className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                       </div>
                       <Button type="submit" className="w-full gap-2 font-body font-semibold text-lg py-6 mt-4 text-white shadow-md border-none" style={{ background: party.color }}>
                         <Send className="w-5 h-5" /> {l.sendTo} {messageCandidate.name}
                       </Button>
                     </form>
                   </DialogContent>
                 </Dialog>
               )}
            </div>
            
          </motion.div>

          {/* Principes et propositions du parti */}
          {(pData.partyPrinciples?.length || displayPartyFocus) ? (
            <motion.div className="bg-card rounded-2xl border border-border shadow-sm mb-10 overflow-hidden" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <div className="p-6 md:p-8 relative z-10">
                <h2 className="font-display font-bold text-xl md:text-2xl text-foreground mb-6 flex items-center justify-center md:justify-start gap-2 pb-4 border-b border-border/50">
                  <Lightbulb className="w-6 h-6 text-primary" /> {l.partyPrinciples}
                </h2>
                
                {pData.partyPrinciples && pData.partyPrinciples.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {pData.partyPrinciples.map((principle: any, idx: number) => (
                      <div key={idx} className="bg-muted/20 p-5 rounded-xl border border-border/50 hover:shadow-md transition-shadow relative overflow-hidden group">
                        <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: party.color }}></div>
                        <h3 className="font-display font-bold text-lg mb-2 text-foreground group-hover:text-primary transition-colors">{principle.title}</h3>
                        <p className="font-body text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{principle.description}</p>
                        {principle.readMoreUrl && (
                          <a href={principle.readMoreUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-primary hover:underline" style={{ color: party.color }}>
                            {l.readMore} <ArrowRight className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="text-left rtl:text-right space-y-3 mb-8">
                    {displayPartyFocus.split(",").map((item, idx) => (
                      <li key={idx} className="font-body text-base md:text-lg font-medium text-muted-foreground flex items-center gap-3 bg-muted/20 p-3 rounded-lg border border-border/50">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: party.color }}></div>
                        {item.trim()}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Boutons Structures / Organisations */}
                <div className="mt-8 pt-6 border-t border-border/50">
                  <h3 className="font-display font-bold text-lg mb-4 text-center md:text-left">{l.structures}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Button 
                      size="lg" 
                      asChild={!!pData.jeunesseUrl} 
                      disabled={!pData.jeunesseUrl}
                      className={`w-full font-body font-semibold shadow-sm transition-all h-14 ${!pData.jeunesseUrl ? 'opacity-50 cursor-not-allowed bg-muted text-muted-foreground' : 'bg-amber-500 hover:bg-amber-600 text-white hover:shadow-md hover:-translate-y-0.5'}`}
                    >
                      {pData.jeunesseUrl ? (
                        <a href={pData.jeunesseUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                          <Users className="w-5 h-5" /> {l.youth}
                        </a>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Users className="w-5 h-5" /> {l.youthNotSet}
                        </span>
                      )}
                    </Button>

                    <Button 
                      size="lg" 
                      asChild={!!pData.femmesUrl} 
                      disabled={!pData.femmesUrl}
                      className={`w-full font-body font-semibold shadow-sm transition-all h-14 ${!pData.femmesUrl ? 'opacity-50 cursor-not-allowed bg-muted text-muted-foreground' : 'bg-pink-500 hover:bg-pink-600 text-white hover:shadow-md hover:-translate-y-0.5'}`}
                    >
                      {pData.femmesUrl ? (
                        <a href={pData.femmesUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                          <Users className="w-5 h-5" /> {l.women}
                        </a>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Users className="w-5 h-5" /> {l.womenNotSet}
                        </span>
                      )}
                    </Button>

                    <Button 
                      size="lg" 
                      asChild={!!pData.academieUrl} 
                      disabled={!pData.academieUrl}
                      className={`w-full font-body font-semibold shadow-sm transition-all h-14 ${!pData.academieUrl ? 'opacity-50 cursor-not-allowed bg-muted text-muted-foreground' : 'bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow-md hover:-translate-y-0.5'}`}
                    >
                      {pData.academieUrl ? (
                        <a href={pData.academieUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                          <BookOpen className="w-5 h-5" /> {l.academy}
                        </a>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <BookOpen className="w-5 h-5" /> {l.academyNotSet}
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : null}

          {/* Boîte à questions publique */}
          {globalQaEnabled && !blockedParties.includes(party.acronym) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-xl text-foreground flex items-center gap-2 px-2">
                <MessageCircle className="w-5 h-5 text-primary" /> {l.qaBox}
              </h2>
              <Button variant="outline" size="sm" onClick={() => setIsQaModalOpen(true)} className="rounded-full shadow-sm text-xs font-semibold gap-2 border-primary/20 hover:bg-primary/5 text-primary">
                <MessageCircle className="w-4 h-4" /> {l.askQuestion}
              </Button>
            </div>

            {publicQa.length === 0 ? (
              <div className="bg-card rounded-3xl border border-border/60 p-6 text-center text-muted-foreground shadow-sm">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-20" />
                <p className="font-body text-sm">{l.noQuestions}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {publicQa.map(qa => (
                  <div key={qa.id} className="bg-card rounded-2xl border border-border/60 p-5 shadow-sm">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <Users className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        {/* Anonymisation */}
                        <h4 className="font-display font-semibold text-sm">{qa.senderFirstName} {qa.senderLastName.charAt(0)}.</h4>
                        <p className="text-xs text-muted-foreground font-body">{new Date(qa.date).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                    <p className="font-body text-sm text-foreground mb-4 font-medium">« {qa.message} »</p>
                    
                    {qa.publicReply && (
                      <div className="ml-6 pl-4 border-l-2 border-border/60 relative">
                        <div className="absolute -left-[17px] top-0 w-8 h-8 rounded-full bg-background border border-border/60 flex items-center justify-center">
                           {displayLogoUrl ? (
                              <img src={displayLogoUrl} alt="Logo" className="w-5 h-5 object-contain" />
                            ) : (
                              <span className="text-xs">{party.symbol}</span>
                           )}
                        </div>
                        <div className="bg-muted/30 rounded-xl p-4 ml-2">
                          <h5 className="font-display font-semibold text-sm mb-1" style={{ color: party.color }}>{l.officialReply} - {partyName}</h5>
                          <p className="font-body text-sm text-muted-foreground whitespace-pre-wrap">{qa.publicReply}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
          )}

          {/* Modal Poser une Question Publique */}
          <Dialog open={isQaModalOpen} onOpenChange={setIsQaModalOpen}>
             <DialogContent className="max-w-xl w-[95vw] rounded-3xl p-0 shadow-2xl border-none overflow-hidden">
                <div className="h-2 w-full" style={{ background: party.color }} />
                <DialogHeader className="px-6 pt-6 pb-2 text-left">
                  <DialogTitle className="font-display text-2xl flex items-center gap-2 mb-2 font-bold" style={{ color: party.color }}>
                    <MessageCircle className="w-6 h-6" />
                    {l.askPublicQuestion}
                  </DialogTitle>
                  <DialogDescription className="font-body text-muted-foreground">
                    {l.qaDesc}
                  </DialogDescription>
                </DialogHeader>
               <form onSubmit={(e) => {
                 e.preventDefault();
                 const fd = new FormData(e.currentTarget);
                 saveInteraction({
                   type: "public_question",
                   candidateName: "Boîte à questions",
                   partyAcronym: party.acronym,
                   senderLastName: fd.get("lastName") as string,
                   senderFirstName: fd.get("firstName") as string,
                   senderEmail: fd.get("email") as string,
                   senderPhone: "",
                   region: "",
                   province: "",
                   subject: "Question Publique",
                   message: fd.get("message") as string,
                 });
                 toast({
                   title: "Question envoyée",
                   description: "Votre question a été transmise aux modérateurs pour validation.",
                 });
                 setIsQaModalOpen(false);
               }} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar bg-card">
                 <div className="grid md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.lastName || "Nom"} *</label>
                     <input required name="lastName" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                   </div>
                   <div>
                     <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.firstName || "Prénom"} *</label>
                     <input required name="firstName" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                   </div>
                 </div>
                 <div>
                   <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.email || "Email"} * <span className="text-muted-foreground font-normal text-xs">{l.emailNotPublished}</span></label>
                   <input required name="email" type="email" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                 </div>
                 <div>
                   <label className="block text-sm font-body font-medium mb-1.5 text-foreground">{t.engagement?.message || "Votre Question"} *</label>
                   <textarea required name="message" placeholder={l.qaPlaceholder} className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1" style={{ '--tw-ring-color': party.color } as any} />
                 </div>
                 <div className="flex items-start gap-3 mt-2">
                   <Checkbox required id="terms-qa" className="mt-1" />
                   <label htmlFor="terms-qa" className="text-sm font-body text-foreground leading-relaxed cursor-pointer">
                     {l.qaTerms}
                   </label>
                 </div>
                 <Button type="submit" className="w-full gap-2 font-body font-semibold text-lg py-6 mt-4 text-white shadow-md border-none" style={{ background: party.color }}>
                   <Send className="w-5 h-5" />
                   {l.submitQa}
                 </Button>
               </form>
             </DialogContent>
          </Dialog>

          {/* Party Events */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-display font-bold text-xl text-foreground mb-5 flex items-center gap-2 px-2">
              <Calendar className="w-5 h-5 text-primary" />{l.events}
            </h2>
            {partyEvents.length === 0 ? (
              <div className="bg-card rounded-3xl border border-border border-dashed p-8 text-center text-muted-foreground">
                 <Calendar className="w-10 h-10 mx-auto mb-3 opacity-20" />
                 <p className="font-body text-sm font-medium">{l.noEvents}</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {partyEvents.map(event => (
                  <Card key={event.id} className="shadow-sm hover:shadow-card transition-shadow border-border/80 rounded-2xl overflow-hidden group">
                    <CardContent className="p-5">
                      <Link to={`/evenement/${event.id}`} className="hover:text-primary transition-colors flex items-center justify-between mb-3">
                        <h3 className="font-display font-bold text-foreground text-md leading-tight max-w-[85%]">{event.title}</h3>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-transform rtl:hidden" />
                        <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:-translate-x-1 transition-transform rtl:block hidden" />
                      </Link>
                      <div className="flex flex-wrap items-center gap-4 text-xs mt-2 text-muted-foreground font-body bg-muted/40 p-2 rounded-lg w-max">
                        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-primary/70" />{event.date}</span>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary/70" />{event.location}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>

          <div className="text-center mt-12 pb-4">
            <Button variant="outline" className="font-body font-semibold rounded-full hover:bg-muted" asChild>
              <Link to="/agenda-national">
                {l.eventsButton} <ArrowRight className="w-4 h-4 ml-2 rtl:mr-2 rtl:ml-0 rtl:rotate-180" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PartiDetailPage;
